/* eslint-env browser */
import React, {
  useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loading from '../../../general/Loading';

export default function IFrame({
  title,
  url,
  height,
  width,
  onContinue,
  onClose,
  onPostEvent,
  showLoading,
  showContinueButton,
}) {
  const { t } = useTranslation(null, { useSuspense: false });
  const iframeRef = useRef(null);

  const handlePostMessage = useCallback((evt) => {
    if (onPostEvent) {
      onPostEvent(evt);
    }
  }, [onPostEvent]);

  useEffect(() => {
    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [handlePostMessage]);

  return (
    <div
      id="cashout-iframe"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Header panel */}
      <div
        className="cashout-iframe-header"
      >
        <h4 style={{ margin: 0, fontSize: 16 }}>{title}</h4>
        <button
          className="cashout-iframe-close"
          type="button"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Iframe body */}
      <div
        className="cashout-iframe-content"
        style={{
          flex: 1,
          minHeight: 0, // important for iframe scrolling
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: url ? undefined : 'flex',
          alignItems: url ? undefined : 'center',
          justifyContent: url ? undefined : 'center',
        }}
      >
        {url ? (
          <iframe
            ref={iframeRef}
            title={title}
            src={url}
            sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
            allow="clipboard-write"
            referrerPolicy="no-referrer"
            style={{
              width,
              height,
              border: 'none',
              display: 'block',
            }}
          />
        ) : (
          <Loading />
        )}
      </div>

      {showLoading && (
        <div className="cashout-iframe-bottom">
          {showLoading && <Loading />}
        </div>
      )}

      {(!showLoading && showContinueButton) && (
        <div className="cashout-iframe-bottom">
          {showContinueButton && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onContinue}
              disabled={!onContinue}
            >
              {t('cashout_continue')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

IFrame.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func,
  onPostEvent: PropTypes.func,
  showLoading: PropTypes.bool,
  showContinueButton: PropTypes.bool,
  height: PropTypes.string,
  width: PropTypes.string,
};

IFrame.defaultProps = {
  url: '',
  onContinue: undefined,
  onPostEvent: undefined,
  showLoading: false,
  showContinueButton: false,
  height: '100%',
  width: '100%',
};
